import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, Crown, Sparkles, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import api, { notifyError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

function fmtDate(iso, language) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleDateString(language === "hi" ? "hi-IN" : "en-IN", { day: "numeric", month: "short", year: "numeric" });
}

// Genuine regular monthly prices (display only). Charged amount stays at the
// 3-month introductory price = tier.price_monthly (unchanged).
const REGULAR_PRICE = { basic: 399, plus: 499, pro: 999 };


export default function Membership() {
  const { t, language } = useLanguage();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tiers, setTiers] = useState([]);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");
  const [checkout, setCheckout] = useState(null); // tier object being purchased
  const [success, setSuccess] = useState(null); // { name, ai_per_day } after activation
  const [showCancel, setShowCancel] = useState(false);

  const isAuthed = user && typeof user === "object";
  const currentTier = isAuthed ? user.membership_tier || "free" : "free";

  const loadStatus = useCallback(() => {
    if (!isAuthed) return;
    api.get("/membership/status").then(({ data }) => setStatus(data)).catch(() => setStatus(null));
  }, [isAuthed]);

  useEffect(() => {
    api.get("/membership/tiers").then(({ data }) => setTiers(data.tiers)).catch(() => setTiers([]));
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Deep-link from the limit modal: /membership?upgrade=basic opens checkout.
  useEffect(() => {
    const want = params.get("upgrade");
    if (!want || !tiers.length) return;
    const tier = tiers.find((x) => x.id === want);
    if (tier && want !== "free") {
      if (isAuthed) setCheckout(tier);
      else navigate("/login");
    }
    params.delete("upgrade");
    setParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiers]);

  const loadRazorpay = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const payWithRazorpay = async (tier) => {
    if (!isAuthed) { navigate("/login"); return; }
    if (!tier) return;
    const name = language === "hi" ? tier.name_hi : tier.name_en;
    const payErr = language === "hi"
      ? "भुगतान पूरा नहीं हो सका। कृपया फिर से प्रयास करें।"
      : "Payment could not be completed. Please try again.";
    setBusy(tier.id);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast.error(payErr); setBusy(""); return; }
      const { data: sub } = await api.post("/membership/create-subscription", { tier: tier.id });
      const rzp = new window.Razorpay({
        key: sub.key_id || process.env.REACT_APP_RAZORPAY_KEY_ID,
        subscription_id: sub.subscription_id,
        name: "EVERMIN",
        description: `EVERMIN ${name} — ₹${tier.price_monthly}/month`,
        prefill: { name: user?.name || "", email: user?.email || "" },
        theme: { color: "#0f172a" },
        handler: async (resp) => {
          try {
            const { data } = await api.post("/membership/verify-subscription", {
              tier: tier.id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_subscription_id: resp.razorpay_subscription_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setUser((prev) => ({ ...prev, membership_tier: data.tier }));
            setCheckout(null);
            try { localStorage.setItem("evermin_welcomed", tier.id); } catch (_) {}
            setSuccess({ name, ai_per_day: data.ai_per_day ?? tier.ai_per_day });
            loadStatus();
          } catch (e) {
            notifyError(e, t);
          } finally {
            setBusy("");
          }
        },
        modal: { ondismiss: () => setBusy("") },
      });
      rzp.on("payment.failed", (r) => {
        toast.error(r?.error?.description || payErr);
        setBusy("");
      });
      rzp.open();
    } catch (e) {
      notifyError(e, t);
      setBusy("");
    }
  };

  const cancel = async () => {
    setBusy("cancel");
    try {
      await api.post("/membership/cancel");
      setShowCancel(false);
      toast.success(t("plan_cancelled"));
      loadStatus();
    } catch (e) {
      notifyError(e, t);
    } finally { setBusy(""); }
  };

  const downgrade = async () => {
    setBusy("free");
    try {
      const { data } = await api.post("/membership/change", { tier: "free" });
      setUser((prev) => ({ ...prev, membership_tier: data.membership_tier || "free" }));
      toast.success(t("downgrade_free"));
      loadStatus();
    } catch (e) {
      notifyError(e, t);
    } finally { setBusy(""); }
  };

  const membership = status?.membership;
  const cancelling = membership?.cancel_at_period_end;
  const currentName = (tiers.find((x) => x.id === currentTier) || {})[language === "hi" ? "name_hi" : "name_en"] || "Free";

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-12 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl">{t("membership_title")}</h1>
          <p className="mx-auto mt-2 max-w-md text-slate-500">{t("membership_subtitle")}</p>
          <p className="mx-auto mt-3 max-w-xl text-sm text-slate-500" data-testid="launch-convert-msg">{t("launch_convert_msg")}</p>
        </div>

        <div className="mx-auto mt-6 flex max-w-2xl items-start gap-2.5 rounded-2xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-left" data-testid="launch-banner">
          <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
          <p className="text-sm text-amber-900">
            <span className="font-semibold">{t("launch_badge")}</span> — {t("launch_banner")}
          </p>
        </div>

        {isAuthed && (
          <div className="mx-auto mt-6 flex max-w-md items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm" data-testid="current-status">
            {currentTier !== "free" ? <Crown className="h-4 w-4 text-amber-500" /> : <ShieldCheck className="h-4 w-4 text-slate-400" />}
            <span className="font-medium text-slate-700">{t("you_are_on")} EVERMIN {currentName}</span>
            {currentTier !== "free" && membership?.renewal_date && (
              <span className="text-slate-400">· {cancelling ? t("cancelling_note") : t("renews_on")} {fmtDate(membership.renewal_date, language)}</span>
            )}
          </div>
        )}

        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {tiers.map((tier) => {
            const paid = tier.id !== "free";
            const name = language === "hi" ? tier.name_hi : tier.name_en;
            const tagline = language === "hi" ? tier.tagline_hi : tier.tagline_en;
            const features = language === "hi" ? tier.features_hi : tier.features_en;
            const isCurrent = isAuthed && currentTier === tier.id;
            const dark = tier.id === "plus"; // subtle emphasis on the middle power tier
            return (
              <div key={tier.id} data-testid={`tier-${tier.id}`}
                className={`relative flex flex-col rounded-3xl border p-6 transition-all duration-200 ${dark ? "border-slate-900 bg-slate-950 text-white shadow-premium-lg lg:-translate-y-1" : "border-slate-200/80 bg-white shadow-premium hover:-translate-y-1"} ${isCurrent ? "ring-2 ring-amber-400" : ""}`}>
                {tier.id === "free" && (
                  <span className="absolute -top-2.5 right-4 rounded-full bg-amber-400 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-900 shadow-sm" data-testid="free-launch-badge">
                    {t("launch_badge")}
                  </span>
                )}
                <h2 className={`font-display text-lg font-bold ${dark ? "text-white" : "text-slate-900"}`}>{name}</h2>
                <p className={`mt-1 text-sm ${dark ? "text-slate-300" : "text-slate-500"}`}>{tagline}</p>

                <div className="mt-4">
                  {REGULAR_PRICE[tier.id] && (
                    <p className={`text-sm font-medium line-through ${dark ? "text-slate-400" : "text-slate-400"}`} data-testid={`regular-price-${tier.id}`}>
                      ₹{REGULAR_PRICE[tier.id]}{t("per_month")}
                    </p>
                  )}
                  <p className="font-display text-4xl font-extrabold tracking-tight" data-testid={`price-${tier.id}`}>
                    ₹{tier.price_monthly}
                    <span className={`ml-0.5 text-sm font-medium ${dark ? "text-slate-300" : "text-slate-400"}`}>{t("per_month")}</span>
                  </p>
                  {REGULAR_PRICE[tier.id] && (
                    <p className={`mt-1.5 inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${dark ? "bg-amber-400/15 text-amber-300" : "bg-emerald-50 text-emerald-700"}`} data-testid={`intro-label-${tier.id}`}>
                      {t("intro_offer")}
                    </p>
                  )}
                  <p className={`mt-1.5 text-xs font-semibold uppercase tracking-wide ${dark ? "text-amber-300" : "text-primary"}`} data-testid={`ai-${tier.id}`}>
                    {tier.ai_per_day} {t("ai_per_day_label")}
                  </p>
                </div>

                <ul className="mt-5 flex-1 space-y-2.5">
                  {features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className={`mt-0.5 h-4 w-4 flex-shrink-0 ${dark ? "text-amber-400" : "text-emerald-500"}`} />
                      <span className={dark ? "text-slate-200" : "text-slate-600"}>{f}</span>
                    </li>
                  ))}
                </ul>

                {tier.id === "free" && (
                  <p className="mt-3 text-xs text-slate-400" data-testid="free-launch-support">{t("launch_free_support")} {t("upgrade_to_keep")}.</p>
                )}

                <div className="mt-6">
                  {isCurrent ? (
                    <div className="space-y-2">
                      <div className={`flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-sm font-semibold ${dark ? "bg-amber-400/15 text-amber-300" : "bg-slate-100 text-slate-600"}`} data-testid={`current-${tier.id}`}>
                        <Check className="h-4 w-4" /> {t("current_plan")}
                      </div>
                      {paid && !cancelling && (
                        <Button variant="ghost" onClick={() => setShowCancel(true)} data-testid="cancel-plan-btn" className={`w-full ${dark ? "text-slate-300 hover:bg-white/10 hover:text-white" : "text-slate-500"}`}>
                          {t("cancel_plan")}
                        </Button>
                      )}
                      {paid && cancelling && <p className={`text-center text-xs ${dark ? "text-slate-400" : "text-slate-400"}`}>{t("plan_cancelled")}</p>}
                    </div>
                  ) : tier.id === "free" ? (
                    isAuthed ? (
                      <Button variant="outline" onClick={downgrade} disabled={busy === "free"} data-testid="downgrade-btn" className="min-h-[46px] w-full rounded-xl font-semibold">
                        {busy === "free" ? t("processing") : t("downgrade_free")}
                      </Button>
                    ) : (
                      <Button onClick={() => navigate("/signup")} data-testid="start-free-btn" className="min-h-[46px] w-full rounded-xl bg-slate-900 font-semibold text-white hover:bg-slate-800">
                        {t("start_free")}
                      </Button>
                    )
                  ) : (
                    <Button onClick={() => (isAuthed ? setCheckout(tier) : navigate("/login"))} data-testid={`choose-${tier.id}`}
                      className={`min-h-[46px] w-full rounded-xl font-semibold ${dark ? "bg-amber-400 text-slate-900 hover:bg-amber-300" : "bg-slate-900 text-white hover:bg-slate-800"}`}>
                      <Sparkles className="mr-1.5 h-4 w-4" /> {t("upgrade_cta")}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <p className="mx-auto mt-8 max-w-xl text-center text-sm text-slate-400" data-testid="payments-note">{t("sandbox_note")}</p>
      </main>
      <Footer />

      {/* Sandbox checkout */}
      <Dialog open={!!checkout} onOpenChange={(o) => !o && setCheckout(null)}>
        <DialogContent data-testid="checkout-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Crown className="h-5 w-5 text-amber-500" /> {t("upgrade_to")} EVERMIN {checkout && (language === "hi" ? checkout.name_hi : checkout.name_en)}</DialogTitle>
            <DialogDescription>{language === "hi" ? "भुगतान Razorpay द्वारा सुरक्षित रूप से प्रोसेस किया जाता है।" : "Payments are processed securely by Razorpay."}</DialogDescription>
          </DialogHeader>
          {checkout && (
            <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 p-4">
              <span className="font-medium text-slate-700">EVERMIN {language === "hi" ? checkout.name_hi : checkout.name_en} · {checkout.ai_per_day} {t("ai_per_day_label")}</span>
              <span className="font-display text-xl font-bold text-slate-900">₹{checkout.price_monthly}{t("per_month")}</span>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCheckout(null)}>{t("close")}</Button>
            <Button onClick={() => payWithRazorpay(checkout)} disabled={busy === checkout?.id} data-testid="confirm-upgrade-btn" className="bg-amber-400 font-semibold text-slate-900 hover:bg-amber-300">
              {busy === checkout?.id ? t("processing") : <><ShieldCheck className="mr-1.5 h-4 w-4" /> {t("upgrade_now")}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Membership activated — success popup */}
      <Dialog open={!!success} onOpenChange={(o) => !o && setSuccess(null)}>
        <DialogContent data-testid="welcome-dialog" className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-400/15 text-amber-500">
              <Crown className="h-7 w-7" />
            </div>
            <DialogTitle className="font-display text-center text-2xl" data-testid="welcome-title">{t("welcome_title")}</DialogTitle>
            <DialogDescription className="text-center text-slate-600" data-testid="welcome-thanks">{t("welcome_thanks")}</DialogDescription>
          </DialogHeader>
          {success && (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-center">
              <p className="font-display text-base font-semibold text-slate-900" data-testid="welcome-plan">
                {t("welcome_ready").replace("{plan}", `EVERMIN ${success.name}`)}
              </p>
              <p className="mt-1.5 flex items-center justify-center gap-1.5 text-sm font-medium text-primary" data-testid="welcome-ai-limit">
                <Sparkles className="h-4 w-4" /> {t("welcome_ai_limit").replace("{count}", success.ai_per_day)}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setSuccess(null)} data-testid="welcome-cta-btn" className="w-full rounded-xl bg-slate-900 font-semibold text-white hover:bg-slate-800">
              {t("welcome_cta")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel confirm */}
      <Dialog open={showCancel} onOpenChange={setShowCancel}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><X className="h-5 w-5 text-rose-500" /> {t("cancel_plan")}</DialogTitle>
            <DialogDescription>{t("cancel_confirm")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCancel(false)}>{t("keep_plan")}</Button>
            <Button onClick={cancel} disabled={busy === "cancel"} data-testid="confirm-cancel-btn" variant="outline" className="text-rose-600 hover:bg-rose-50">
              {busy === "cancel" ? t("processing") : t("cancel_plan")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
